import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import notificationService, { AppNotification } from '../services/notificationService';
import authService from '../services/auth';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  addNotification: (notification: AppNotification) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => void;
  refreshNotifications: () => Promise<void>;
  clearError: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Récupérer le token depuis authService
  const token = authService.getAccessToken();

  // Charger les notifications initiales
  const loadNotifications = async () => {
    if (!user) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [notificationsData, unreadCountData] = await Promise.all([
        notificationService.getNotifications({ limit: 50 }),
        notificationService.getUnreadCount()
      ]);

      // Gérer la réponse paginée ou simple
      const notifications = Array.isArray(notificationsData) 
        ? notificationsData 
        : notificationsData.results || [];

      setNotifications(notifications);
      setUnreadCount(unreadCountData);
      
      // Log pour vérifier l'état après mise à jour
      
    } catch (err: any) {
      console.error('🔔 Erreur lors du chargement des notifications:', err);
      setError('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  // Ajouter une nouvelle notification
  const addNotification = (notification: AppNotification) => {
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);
  };

  // Marquer une notification comme lue
  const markAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, read: true }
            : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Erreur lors du marquage de la notification comme lue:', err);
      setError('Erreur lors du marquage de la notification');
    }
  };

  // Marquer toutes les notifications comme lues
  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, read: true }))
      );
      setUnreadCount(0);
    } catch (err: any) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', err);
      setError('Erreur lors du marquage des notifications');
    }
  };

  // Supprimer une notification
  const deleteNotification = async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      console.error('Erreur lors de la suppression de la notification:', err);
      setError('Erreur lors de la suppression de la notification');
    }
  };

  // Actualiser les notifications
  const refreshNotifications = async () => {
    await loadNotifications();
  };

  // Effacer l'erreur
  const clearError = () => {
    setError(null);
  };

  // Initialiser la connexion WebSocket et charger les notifications
  useEffect(() => {
    
    if (user && token) {
      
      // Réinitialiser les notifications à chaque connexion
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      
      // Charger les notifications initiales
      loadNotifications();

      // Configurer le listener pour les nouvelles notifications
      const handleNewNotification = (notification: AppNotification) => {
        addNotification(notification);
        
        // Afficher une notification toast si le navigateur le supporte
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(notification.title, {
            body: notification.message,
            icon: '/images/logo-pontcita.png',
            tag: notification.id
          });
        }
      };

      notificationService.addListener(handleNewNotification);

      // Connecter le WebSocket (désactivé temporairement)
      // notificationService.connect(user.id.toString(), token);

      // Système de polling pour les nouvelles notifications (toutes les 30 secondes)
      const pollInterval = setInterval(async () => {
        try {
          const currentUnreadCount = await notificationService.getUnreadCount();
          if (currentUnreadCount > unreadCount) {
            // Il y a de nouvelles notifications, recharger la liste
            loadNotifications();
          }
        } catch (error) {
          console.warn('Erreur lors du polling des notifications:', error);
        }
      }, 30000); // 30 secondes

      // Demander la permission pour les notifications du navigateur
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }

      // Nettoyage
      return () => {
        notificationService.removeListener(handleNewNotification);
        notificationService.disconnect();
        clearInterval(pollInterval);
      };
    } else {
      console.log('🔔 NotificationProvider: Pas d\'utilisateur ou de token, pas de chargement des notifications');
    }
  }, [user, token]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    clearError
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
